import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, query, where, getDocs, updateDoc, doc, arrayUnion, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useFormattedNumber } from '../hooks/useFormattedNumber';
import { colors, commonStyles } from '../styles/colors';
import BaseModal from './common/BaseModal';
import './AddHouseModal.css';

const HOUSE_STATUSES = {
  UNDERWRITING: 'Underwriting',
  UNQUALIFIED: 'Unqualified',
  UNDER_CONTRACT: 'Under Contract',
  UNDER_MANAGEMENT: 'Under Management',
  DIVESTED: 'Divested'
};

const AddHouseModal = ({ isOpen, onClose, onAdd }) => {
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [listPrice, setListPrice] = useState('');
  const [status, setStatus] = useState('UNDERWRITING');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [userSettings, setUserSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // Fetch user settings when the modal opens
  useEffect(() => {
    const fetchUserSettings = async () => {
      if (isOpen && currentUser?.uid) {
        try {
          setLoadingSettings(true);
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            setUserSettings({
              defaultCapRate: userData.defaultCapRate || '',
              defaultCoCReturn: userData.defaultCoCReturn || '',
              defaultTotalROI: userData.defaultTotalROI || ''
            });
          } else {
            setUserSettings(null);
          }
        } catch (error) {
          console.error('Error fetching user settings:', error);
          setUserSettings(null);
        } finally {
          setLoadingSettings(false);
        }
      }
    };

    fetchUserSettings();
  }, [isOpen, currentUser]);

  // Function to check if all required fields are filled
  const areAllFieldsFilled = () => {
    return street.trim() !== '' && 
           city.trim() !== '' && 
           state.trim() !== '' && 
           zipCode.trim() !== '' &&
           listPrice.trim() !== '';
  };

  const formatPrice = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Format with commas
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const handlePriceChange = (e) => {
    const rawValue = e.target.value;
    const formattedValue = formatPrice(rawValue);
    setListPrice(formattedValue);
  };

  useEffect(() => {
    if (isOpen) {
      setStreet('');
      setCity('');
      setState('');
      setZipCode('');
      setListPrice('');
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    console.log('Starting form submission');

    // Validate required fields
    if (!listPrice) {
      setError('Please enter a list price');
      console.log('Validation failed: No list price');
      return;
    }

    if (!street || !city || !state || !zipCode) {
      setError('Please fill in all address fields');
      return;
    }

    try {
      console.log('Creating property data object');
      // Create base property data
      const propertyData = {
        address: {
          street: street.trim(),
          city: city.trim(),
          state: state.trim(),
          zipCode: zipCode.trim(),
          latitude: null,
          longitude: null,
          country: 'USA'
        },
        listPrice: parseFloat(listPrice.replace(/,/g, '')),
        status,
        userId: currentUser.uid,
        createdBy: currentUser.uid,
        createdAt: new Date().toISOString()
      };
      
      // Add default target values from user settings if they exist
      if (userSettings) {
        console.log('Applying default target values from user settings');
        if (userSettings.defaultCapRate) {
          propertyData.targetCapRate = userSettings.defaultCapRate;
        }
        if (userSettings.defaultCoCReturn) {
          propertyData.targetCoCReturn = userSettings.defaultCoCReturn;
        }
        if (userSettings.defaultTotalROI) {
          propertyData.targetTotalROI = userSettings.defaultTotalROI;
        }
      }

      console.log('Property data:', propertyData);
      setIsLoading(true);

      try {
        // Create a new property document
        console.log('Adding property to properties collection');
        const propertyRef = await addDoc(collection(db, 'properties'), propertyData);
        console.log('Property added with ID:', propertyRef.id);

        try {
          // Add to user's properties collection - wrap in separate try/catch
          // so if this fails, we still proceed with the rest of the function
          console.log('Adding property to user\'s properties collection');
          await addDoc(collection(db, 'users', currentUser.uid, 'properties'), {
            propertyId: propertyRef.id,
            address: propertyData.address,
            listPrice: propertyData.listPrice
          });
          console.log('Property added to user\'s collection');
        } catch (userPropError) {
          // Just log this error but continue - it's not critical
          console.warn('Could not add to user properties subcollection:', userPropError);
        }

        if (onAdd) {
          console.log('Calling onAdd callback');
          onAdd({
            id: propertyRef.id,
            address: propertyData.address,
            listPrice: propertyData.listPrice,
            status: propertyData.status
          });
        }
        
        console.log('Form submission completed successfully');
        setIsLoading(false);
        clearForm();
        onClose();
        
        // Show success message
        setSuccessMessage('Property created successfully!');
        
        // Use React Router navigation after a short delay
        // The delay gives Firestore time to propagate permissions
        setTimeout(() => {
          // Navigate to the property detail page
          navigate(`/property/${propertyRef.id}`);
        }, 800);
      } catch (mainError) {
        console.error('Error creating property:', mainError);
        throw mainError; // Re-throw to be caught by the outer catch block
      }
    } catch (error) {
      console.error('Detailed error:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      setError(`Error adding property: ${error.message}`);
      setIsLoading(false);
    }
  };

  const clearForm = () => {
    setStreet('');
    setCity('');
    setState('');
    setZipCode('');
    setListPrice('');
    setError('');
  };

  const modalFooter = (
    <>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={onClose}
        disabled={isLoading}
      >
        Cancel
      </button>
      <button
        type="submit"
        className="btn btn-primary"
        disabled={isLoading || !areAllFieldsFilled()}
        onClick={handleSubmit}
      >
        {isLoading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Adding...
          </>
        ) : (
          'Add House'
        )}
      </button>
    </>
  );

  if (!isOpen) return null;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add New House"
      footer={modalFooter}
      maxWidth="600px"
      width="90%"
    >
      <form className="needs-validation" noValidate onKeyDown={(e) => {
          if (e.key === 'Enter' && areAllFieldsFilled() && !isLoading) {
            e.preventDefault();
            handleSubmit(e);
          }
        }}>
        <div className="mb-3">
          <div className="address-fields">
            <div className="mb-3">
              <label className="form-label">Street Address <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control text-light border-secondary"
                style={{ background: colors.background.light }}
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                placeholder="123 Main St"
                required
              />
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">City <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control text-light border-secondary"
                  style={{ background: colors.background.light }}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                  required
                />
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label">State <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control text-light border-secondary"
                  style={{ background: colors.background.light }}
                  value={state}
                  onChange={(e) => setState(e.target.value.toUpperCase())}
                  placeholder="NC"
                  maxLength="2"
                  required
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">ZIP Code <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control text-light border-secondary"
                  style={{ background: colors.background.light }}
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  placeholder="12345"
                  maxLength="5"
                  required
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">List Price <span className="text-danger">*</span></label>
          <div className="input-group">
            <span className="input-group-text text-light border-secondary" style={{ background: colors.background.light }}>$</span>
            <input
              type="text"
              className="form-control text-light border-secondary"
              style={{ background: colors.background.light }}
              value={listPrice}
              onChange={handlePriceChange}
              placeholder="Enter list price"
              required
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Status</label>
          <select
            className="form-select text-light border-secondary"
            style={{ background: colors.background.light }}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            required
          >
            {Object.entries(HOUSE_STATUSES).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
      </form>
    </BaseModal>
  );
};

export default AddHouseModal;
